import React, { useCallback, useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import {
  DataGrid,
  GridActionsCellItem,
  gridClasses,
  GridDeleteIcon,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { format, isEqual } from "date-fns";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import OrdersOverview from "layouts/dashboard/components/OrdersOverview";
import reportsBarChartData from "layouts/dashboard/data/reportsBarChartData";
import reportsLineChartData from "layouts/dashboard/data/reportsLineChartData";
import { Box, Button, Chip, Divider, Stack, styled, Typography } from "@mui/material";
import { useMemo } from "react";
import { getMyEvents } from "services/contract";
import TablePaginationCustom from "components/Pagination";
import MDButton from "components/MDButton";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getUserAnalytics } from "services/contract";
import { getEventAnalytics } from "services/contract";
import { getEventsById } from "services/contract";
import { getTicketDetailById } from "services/contract";
import DetailedLoader from "skeletonLoader/detailedLoader";
import MDTypography from "components/MDTypography";
import { FaRegCalendarAlt } from "react-icons/fa";
import Moment from "react-moment";
import singhCoinLogo from "../pages/Events/SinghCoin.png";
import { MdOutlinePlace } from "react-icons/md";

const HIDE_COLUMNS = { category: false };

const HIDE_COLUMNS_TOGGLABLE = ["category", "actions"];

// ----------------------------------------------------------------------

function useBoolean(defaultValue = false) {
  const [value, setValue] = useState(defaultValue);

  const onTrue = useCallback(() => {
    setValue(true);
  }, []);

  const onFalse = useCallback(() => {
    setValue(false);
  }, []);

  const onToggle = useCallback(() => {
    setValue((prev) => !prev);
  }, []);

  const memoizedValue = useMemo(
    () => ({
      value,
      onTrue,
      onFalse,
      onToggle,
      setValue,
    }),
    [value, onTrue, onFalse, onToggle, setValue]
  );

  return memoizedValue;
}

function useSetState(initialState) {
  const [state, set] = useState(initialState);

  const canReset = !isEqual(state, initialState);

  const setState = useCallback((updateState) => {
    set((prevValue) => ({ ...prevValue, ...updateState }));
  }, []);

  const setField = useCallback(
    (name, updateValue) => {
      setState({ [name]: updateValue });
    },
    [setState]
  );

  const onResetState = useCallback(() => {
    set(initialState);
  }, [initialState]);

  const memoizedValue = useMemo(
    () => ({
      state,
      setState,
      setField,
      onResetState,
      canReset,
    }),
    [canReset, onResetState, setField, setState, state]
  );

  return memoizedValue;
}

function EmptyContent({
  sx,
  imgUrl,
  action,
  filled,
  slotProps,
  description,
  title = "No data",
  ...other
}) {
  return (
    <Stack
      flexGrow={1}
      alignItems="center"
      justifyContent="center"
      sx={{
        px: 3,
        height: 1,
        ...(filled && {
          borderRadius: 2,
          bgcolor: (theme) => varAlpha(theme.vars.palette.grey["500Channel"], 0.04),
          border: (theme) => `dashed 1px ${varAlpha(theme.vars.palette.grey["500Channel"], 0.08)}`,
        }),
        ...sx,
      }}
      {...other}
    >
      <Box
        component="img"
        alt="empty content"
        src={imgUrl ?? `/assets/icons/empty/ic-content.svg`}
        sx={{ width: 1, maxWidth: 160, ...slotProps?.img }}
      />

      {title && (
        <Typography
          variant="h6"
          component="span"
          sx={{ mt: 1, textAlign: "center", ...slotProps?.title, color: "text.disabled" }}
        >
          {title}
        </Typography>
      )}

      {description && (
        <Typography
          variant="caption"
          sx={{ mt: 1, textAlign: "center", color: "text.disabled", ...slotProps?.description }}
        >
          {description}
        </Typography>
      )}

      {action && action}
    </Stack>
  );
}

function TicketDetails() {
  const { sales, tasks } = reportsLineChartData;
  const navigate = useNavigate();
  const [analyticsData, setAnalyticsData] = useState();

  function applyFilter({ inputData, filters }) {
    const { stock, publish } = filters;

    if (stock.length) {
      inputData = inputData.filter((product) => stock.includes(product.inventoryType));
    }

    if (publish.length) {
      inputData = inputData.filter((product) => publish.includes(product.publish));
    }

    return inputData;
  }

  const confirmRows = useBoolean();

  // const router = useRouter();
  const [loading, setLoading] = useState(false);
  // const { products, productsLoading } = useGetProducts();

  const filters = useSetState({ publish: [], stock: [] });

  const [tableData, setTableData] = useState([]);

  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const [filterButtonEl, setFilterButtonEl] = useState();
  //  (useState < HTMLButtonElement) | (null > null);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(HIDE_COLUMNS);

  const canReset = filters.state.publish.length > 0 || filters.state.stock.length > 0;

  const dataFiltered = applyFilter({ inputData: tableData, filters: filters.state });

  function formatDate(date) {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0"); // Months are zero-based
    const day = String(date.getUTCDate()).padStart(2, "0");
    let hours = date.getUTCHours() + 5; // Add 5 hours for GMT+5
    let minutes = date.getUTCMinutes() + 30; // Add 30 minutes for GMT+5:30

    // Handle overflow for minutes
    let overflowHours = 0;
    if (minutes >= 60) {
      overflowHours = Math.floor(minutes / 60); // Calculate overflow hours
      minutes = minutes % 60; // Get remaining minutes
    }

    // Handle overflow for hours
    hours = (hours + overflowHours) % 24; // Wrap around hours
    const formattedHours = String(hours).padStart(2, "0");
    const formattedMinutes = String(minutes).padStart(2, "0");

    // Construct final formatted date string
    return `${year}-${month}-${day} ${formattedHours}:${formattedMinutes} GMT+5:30`;
  }

  const columns = [
    {
      field: "orderId",
      headerName: "Order",
      flex: 1,
      minWidth: 80,
      //   renderCell: (params) => {
      //     console.log("@params", params);
      //     return params?.row?.orderId;
      //   },
    },
    {
      field: "customer",
      headerName: "Customer",
      width: 160,
      renderCell: (params) => {
        return params?.row?.userId?.username; // Pass valid timestamp to formatDate
      },
      // params.value,
      // (new Date(params.value), "yyyy-MM-dd HH:mm"),
    },
    {
      field: "date",
      headerName: "Date",
      width: 180,
    },
    {
      field: "ticket",
      headerName: "Tickets",
      width: 200,
    },
    {
      field: "price",
      headerName: "Price",
      width: 200,
    },
    // {
    //   field: "action",
    //   headerName: "Actions",
    //   width: 150,
    //   renderCell: (params) => {
    //     console.log("param", params);
    //     return (
    //       <MDButton
    //         size="meduim"
    //         onClick={() =>
    //           navigate(`/event-details/?eventDetails=otherEvent&tokenId=${params?.id}`)
    //         }
    //       >
    //         View
    //       </MDButton>
    //     );
    //   },
    // },
  ];

  const getTogglableColumns = () =>
    columns
      .filter((column) => !HIDE_COLUMNS_TOGGLABLE.includes(column.field))
      .map((column) => column.field);

  function FiltersResult({ totalResults, onReset, sx, children }) {
    return (
      <Box sx={sx}>
        <Box sx={{ mb: 1.5, typography: "body2" }}>
          <strong>{totalResults}</strong>
          <Box component="span" sx={{ color: "text.secondary", ml: 0.25 }}>
            results found
          </Box>
        </Box>

        <Box flexGrow={1} gap={1} display="flex" flexWrap="wrap" alignItems="center">
          {children}

          <Button
            color="error"
            onClick={onReset}
            // startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
          >
            Clear
          </Button>
        </Box>
      </Box>
    );
  }

  function FiltersBlock({ label, children, isShow, sx }) {
    if (!isShow) {
      return null;
    }

    return (
      <Box
        gap={1}
        display="flex"
        sx={{
          p: 1,
          borderRadius: 1,
          overflow: "hidden",
          // border: (theme) => `dashed 1px ${theme.vars.palette.divider}`,
          ...sx,
        }}
      >
        <Box
          component="span"
          sx={{
            height: 24,
            lineHeight: "24px",
            // fontSize: (theme) => theme.typography.subtitle2.fontSize,
            // fontWeight: (theme) => theme.typography.subtitle2.fontWeight,
          }}
        >
          {label}
        </Box>
        <Box gap={1} display="flex" flexWrap="wrap">
          {children}
        </Box>
      </Box>
    );
  }

  function ProductTableFiltersResult({ filters, totalResults, sx }) {
    const handleRemoveStock = useCallback(
      (inputValue) => {
        const newValue = filters.state.stock.filter((item) => item !== inputValue);

        filters.setState({ stock: newValue });
      },
      [filters]
    );

    const handleRemovePublish = useCallback(
      (inputValue) => {
        const newValue = filters.state.publish.filter((item) => item !== inputValue);

        filters.setState({ publish: newValue });
      },
      [filters]
    );

    return (
      <FiltersResult totalResults={totalResults} onReset={filters.onResetState} sx={sx}>
        <FiltersBlock label="Stock:" isShow={!!filters.state.stock.length}>
          {filters.state.stock.map((item) => (
            <Chip
              {...chipProps}
              key={item}
              label={sentenceCase(item)}
              onDelete={() => handleRemoveStock(item)}
            />
          ))}
        </FiltersBlock>

        <FiltersBlock label="Publish:" isShow={!!filters.state.publish.length}>
          {filters.state.publish.map((item) => (
            <Chip
              {...chipProps}
              key={item}
              label={sentenceCase(item)}
              onDelete={() => handleRemovePublish(item)}
            />
          ))}
        </FiltersBlock>
      </FiltersResult>
    );
  }

  function CustomToolbar({
    filters,
    canReset,
    selectedRowIds,
    filteredResults,
    setFilterButtonEl,
    onOpenConfirmDeleteRows,
  }) {
    return (
      <>
        <GridToolbarContainer>
          {/* <ProductTableToolbar
            filters={filters}
            options={{ stocks: PRODUCT_STOCK_OPTIONS, publishs: PUBLISH_OPTIONS }}
          /> */}

          <Stack
            spacing={1}
            flexGrow={1}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            {/* {!!selectedRowIds?.length && (
              <MDButton
                size="small"
                color="error"
                // startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
                onClick={onOpenConfirmDeleteRows}
              >
                Delete ({selectedRowIds?.length})
              </MDButton>
            )} */}
            <GridToolbarQuickFilter />
            <GridToolbarColumnsButton ref={setFilterButtonEl} />
            {/* <Box>
              <GridToolbarColumnsButton
           
              />
              <GridToolbarFilterButton ref={setFilterButtonEl} />
            </Box> */}
            {/* <GridToolbarExport /> */}
          </Stack>
        </GridToolbarContainer>

        {canReset && (
          <ProductTableFiltersResult
            filters={filters}
            totalResults={filteredResults}
            sx={{ p: 2.5, pt: 0 }}
          />
        )}
      </>
    );
  }

  const CustomToolbarCallback = useCallback(
    () => (
      <CustomToolbar
        filters={filters}
        canReset={canReset}
        selectedRowIds={selectedRowIds}
        setFilterButtonEl={setFilterButtonEl}
        filteredResults={dataFiltered?.length}
        onOpenConfirmDeleteRows={confirmRows.onTrue}
      />
    ),

    [filters.state, selectedRowIds]
  );

  const [currentPage, setCurrentPage] = useState(0); // Current page
  const [rowsPerPage, setRowsPerPage] = useState(5); // Rows per page
  const [pageLoading, setPageLoading] = useState(true);
  const [ticketData, setTicketData] = useState({});

  const { id } = useParams();
  console.log("%%%rrr", id);

  const fetchTicketInfo = async () => {
    setPageLoading(true);
    const ticketData = await getTicketDetailById(id);

    console.log("ticketData", ticketData);

    setTicketData(ticketData?.data);
    setPageLoading(false);
    // setTotalPages(eventsData[0]?.soldTo?.length);
  };

  useEffect(() => {
    // fetchEvents(currentPage, rowsPerPage);
    fetchTicketInfo();
    // fetchEventAnalytics();
    // eslint-disable-next-line
  }, [currentPage, rowsPerPage]);

  console.log("#ticketData", ticketData);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {pageLoading ? (
        <>
          <DetailedLoader />
        </>
      ) : (
        <MDBox>
          <MDBox
            sx={{
              marginBottom: "3rem",
              height: { xs: "none", md: "500px" },
              width: "100%",
              backgroundImage: `url(${ticketData?.photo})`,
              objectFit: "contain",
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <Grid
              container
              sx={{
                height: "100%",
                backgroundImage:
                  "linear-gradient(90deg,#1a1a1a,rgba(18, 18, 18, 0.9),rgba(18, 18, 18, 0.8),rgba(18, 18, 18, 0.9),rgba(18, 18, 18, 0.9), #1a1a1a)",
              }}
            >
              <Grid
                item
                container
                xs={12}
                md={4}
                sx={{
                  height: "100%",
                  // marginBottom: { xs: "1rem", md: "none" },
                  padding: "0 20px 0 0",
                  justifyContent: "right",
                  alignItems: "center",
                }}
              >
                <MDBox sx={{ height: "400px", width: "350px" }}>
                  <img
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "10px 10px 10px 10px",
                    }}
                    src={ticketData?.photo}
                    alt="logo"
                  />
                </MDBox>
              </Grid>

              <Grid
                item
                container
                xs={12}
                md={5}
                sx={{
                  paddingLeft: "1rem",
                  // paddingRight: { xs: "1rem", md: "none" },
                  // paddingLeft: { xs: "1rem", md: "none" },
                  alignItems: "center",
                }}
              >
                <MDBox sx={{}}>
                  <MDBox sx={{ width: "100%" }}>
                    <MDBox>
                      {/* <MDTypography fontWeight="bold" sx={{ fontSize: { xs: "30px", md: "20px" } }}>
                        {category} &nbsp; || &nbsp; {remainTicket} tickets remaining
                      </MDTypography> */}
                    </MDBox>
                    <MDBox>
                      <MDTypography
                        fontWeight="bold"
                        sx={{
                          fontSize: { xs: "30px", md: "40px" },
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          // width: "600px",
                          width: { xs: "100%", md: "600px" },
                        }}
                      >
                        {ticketData?.name}
                      </MDTypography>
                    </MDBox>
                    <MDBox>
                      <MDTypography
                        fontWeight="regular"
                        sx={{ fontSize: { xs: "20px", md: "30px" } }}
                      >
                        <FaRegCalendarAlt size={20} />
                        &nbsp;
                        <Moment format="ddd, DD MMMM, YYYY hh:mm A [GMT]Z">
                          {new Date(ticketData?.eventDateTime)}
                        </Moment>
                      </MDTypography>
                    </MDBox>
                    <MDBox>
                      <MDTypography
                        fontWeight="regular"
                        sx={{ fontSize: { xs: "20px", md: "30px" } }}
                      >
                        <MdOutlinePlace size={20} />
                        &nbsp;
                        {ticketData?.venue}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                  <MDBox
                    sx={{
                      marginBottom: "2rem",
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <MDBox sx={{ marginLeft: "-5px", height: 30, width: 30 }}>
                      <img src={singhCoinLogo} style={{ width: "100%", height: "100%" }} />
                    </MDBox>

                    <MDTypography fontSize="30px">
                      {ticketData?.price === "0"
                        ? "Free"
                        : `$${Number(ticketData?.price).toFixed(2)}`}
                    </MDTypography>
                  </MDBox>
                  <MDBox sx={{ width: "100%", display: "flex", gap: "1rem" }}>
                    {/* <MDButton>
                  <MdDeleteOutline />
                </MDButton> */}
                  </MDBox>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>

          <Divider />

          <Grid
            sx={{
              marginBottom: "5rem",

              paddingLeft: { xs: "1rem", md: "8rem" },
              paddingRight: { xs: "1rem", md: "8rem" },
            }}
          >
            <MDBox
              sx={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "0.5rem",
              }}
            >
              <MDTypography variant="h3">Ticket details</MDTypography>
            </MDBox>
            <MDBox sx={{ display: "flex", marginBottom: "0.5rem" }}>
              <MDTypography fontWeight="regular" variant="h5">
                Ticket Id :&nbsp;
              </MDTypography>
              <MDTypography fontWeight="light" variant="h5">
                {ticketData?.soldTo?.[0]?._id}
              </MDTypography>
            </MDBox>
            <MDBox sx={{ display: "flex", marginBottom: "0.5rem" }}>
              <MDTypography fontWeight="regular" variant="h5">
                Sold to :&nbsp;
              </MDTypography>
              <MDTypography fontWeight="light" variant="h5">
                {ticketData?.soldTo?.[0]?.userId?.fullName}
              </MDTypography>
            </MDBox>
            <MDBox sx={{ display: "flex", marginBottom: "0.5rem" }}>
              <MDTypography fontWeight="regular" variant="h5">
                Scan Status :&nbsp;
              </MDTypography>
              <MDTypography fontWeight="light" variant="h5">
                {ticketData?.ticketEntryLog?.verification === "Success" ? "Scanned" : "Not Scanned"}
              </MDTypography>
            </MDBox>
            {ticketData?.ticketEntryLog?.verifiedBy?.fullName && (
              <MDBox sx={{ display: "flex", marginBottom: "0.5rem" }}>
                <MDTypography fontWeight="regular" variant="h5">
                  Verified by :&nbsp;
                </MDTypography>
                <MDTypography fontWeight="light" variant="h5">
                  {ticketData?.ticketEntryLog?.verifiedBy?.fullName}
                </MDTypography>
              </MDBox>
            )}
            {ticketData?.ticketEntryLog?.entryTimestamp && (
              <MDBox sx={{ display: "flex", marginBottom: "0.5rem" }}>
                <MDTypography fontWeight="regular" variant="h5">
                  Scan Time :&nbsp;
                </MDTypography>
                <MDTypography fontWeight="light" variant="h5">
                  <Moment format="DD MMMM YYYY, hh:mm A [GMT]Z">
                    {new Date(ticketData?.ticketEntryLog?.entryTimestamp)}
                  </Moment>
                </MDTypography>
              </MDBox>
            )}

            <MDBox
              sx={{
                display: "flex",
                flexWrap: "wrap",
                wordBreak: "break-word",
                marginBottom: "0.5rem",
              }}
            >
              <MDTypography fontWeight="regular" variant="h5">
                Total Bought Tickets :&nbsp;
              </MDTypography>
              <MDTypography fontWeight="light" variant="h5">
                {ticketData?.soldTo?.[0]?.ticket}
              </MDTypography>
            </MDBox>
            <MDBox sx={{ display: "flex", marginBottom: "0.5rem" }}>
              <MDTypography fontWeight="regular" variant="h5">
                Order Id :&nbsp;
              </MDTypography>
              <MDTypography
                fontWeight="light"
                variant="h5"
                sx={{
                  wordBreak: "break-all", // Ensures the address breaks in the middle if it's too long
                  overflowWrap: "break-word", // Additional safeguard for breaking long words
                }}
              >
                {ticketData?.soldTo?.[0]?.orderId}
              </MDTypography>
            </MDBox>

            <MDBox sx={{ display: "flex", marginBottom: "0.5rem" }}>
              <MDTypography fontWeight="regular" variant="h5">
                Price :&nbsp;
              </MDTypography>
              <MDTypography fontWeight="light" variant="h5">
                ${Number(ticketData?.soldTo?.[0]?.price).toFixed(2)}
              </MDTypography>
            </MDBox>
          </Grid>
        </MDBox>
      )}

      <Footer />
    </DashboardLayout>
  );
}

export default TicketDetails;
